import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link2 } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../../auth';
import { DiscordIcon } from './DiscordIcon';
import './discord.css';

export const DiscordAuthButton = ({ mode = 'login', available, active = true, disabled = false, testId = 'auth-discord', label }) => {
  const { authHeader } = useAuth();
  const [configured, setConfigured] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [checking, setChecking] = useState(active && available === undefined), [configError, setConfigError] = useState(''), [retry, setRetry] = useState(0);
  const inFlight = useRef(false);
  useEffect(() => {
    if (!active || available !== undefined) { setChecking(false); return; }
    const controller = new AbortController();
    setChecking(true); setConfigured(false); setConfigError('');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/discord/config`, { signal: controller.signal, timeout: 10000 })
      .then(({ data }) => { if (!controller.signal.aborted) setConfigured(data.enabled); })
      .catch(() => { if (!controller.signal.aborted) setConfigError('Could not check Discord availability. Please try again.'); })
      .finally(() => { if (!controller.signal.aborted) setChecking(false); });
    return () => controller.abort();
  }, [active, available, retry]);
  const enabled = available ?? configured;
  const start = async () => {
    if (inFlight.current || !enabled) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/discord/start`, { mode }, { headers: mode === 'connect' ? authHeader() : {}, withCredentials: true, timeout: 20000 });
      sessionStorage.setItem('gv_discord_mode', mode);
      window.location.assign(data.authorization_url);
    } catch (failure) {
      setError(formatApiErrorDetail(failure.response?.data?.detail || 'Could not connect to Discord. Please try again.'));
      inFlight.current = false; setBusy(false);
    }
  };
  return <div className="discord-auth-control">
    <button type="button" data-testid={testId} className="discord-button" disabled={!enabled || disabled || busy || checking || !active} onClick={start} title={!checking && !configError && !enabled ? 'Discord is not configured yet' : undefined}>{mode === 'connect' ? <Link2 size={16} aria-hidden="true" data-testid={`${testId}-icon`} /> : <DiscordIcon data-testid={`${testId}-logo`} />}{checking ? 'Checking Discord…' : busy ? 'Connecting…' : label || (mode === 'connect' ? 'Connect Discord' : 'Continue with Discord')}</button>
    {!checking && !configError && !enabled && <p className="discord-status-note" data-testid={`${testId}-unavailable`}>Discord connection is not available yet.</p>}
    {configError && <div><p className="settings-error" role="alert" data-testid={`${testId}-config-error`}>{configError}</p><button type="button" className="discord-config-retry" data-testid={`${testId}-config-retry`} onClick={() => setRetry((value) => value + 1)}>Retry</button></div>}
    {error && <p className="settings-error" role="alert" data-testid={`${testId}-error`}>{error}</p>}
  </div>;
};